import React, { useState, useMemo } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
  Marker,
} from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import { scaleLinear } from "d3-scale";

const US_STATES_TOPO_JSON =
  "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";
const US_COUNTIES_TOPO_JSON =
  "https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json";

const DEFAULT_FILL = "#5860E0";
const DEFAULT_FILL_HOVER = "#4148C9";
const STROKE_COLOR = "#ffffff";


// US state abbreviation <-> full name
const STATE_ABBR_TO_NAME = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri",
  MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio",
  OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
  VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
  DC: "District of Columbia",
};

// US state FIPS <-> abbreviation (us-atlas uses FIPS as the geo id)
const STATE_FIPS_TO_ABBR = {
  "01": "AL", "02": "AK", "04": "AZ", "05": "AR", "06": "CA",
  "08": "CO", "09": "CT", "10": "DE", "11": "DC", "12": "FL",
  "13": "GA", "15": "HI", "16": "ID", "17": "IL", "18": "IN",
  "19": "IA", "20": "KS", "21": "KY", "22": "LA", "23": "ME",
  "24": "MD", "25": "MA", "26": "MI", "27": "MN", "28": "MS",
  "29": "MO", "30": "MT", "31": "NE", "32": "NV", "33": "NH",
  "34": "NJ", "35": "NM", "36": "NY", "37": "NC", "38": "ND",
  "39": "OH", "40": "OK", "41": "OR", "42": "PA", "44": "RI",
  "45": "SC", "46": "SD", "47": "TN", "48": "TX", "49": "UT",
  "50": "VT", "51": "VA", "53": "WA", "54": "WV", "55": "WI",
  "56": "WY",
};
const STATE_ABBR_TO_FIPS = Object.fromEntries(
  Object.entries(STATE_FIPS_TO_ABBR).map(([fips, abbr]) => [abbr, fips])
);

const DEFAULT_CENTER = [-97, 38];
const DEFAULT_ZOOM = 1;
const STATE_ZOOM = 4;

// Normalise a county name for matching: strip suffix words, lowercase, trim.
const normaliseCountyName = (raw = "") =>
  String(raw)
    .toLowerCase()
    .replace(/\s+county$/i, "")
    .replace(/\s+parish$/i, "")
    .replace(/\s+borough$/i, "")
    .replace(/\s+census area$/i, "")
    .replace(/\s+municipality$/i, "")
    .replace(/\s+city and borough$/i, "")
    .replace(/[^a-z0-9]/g, "")
    .trim();

const RevenueByLocationMap = ({
  title = "Inventory by location",
  data = {},
  baseColor = DEFAULT_FILL,
  isLightTheme = true,
  height = 360,
  /** When true, map fills parent flex height (parent must be a flex column with bounded height). */
  fillContainer = false,
  valueFormatter,
  valueLabel = "vehicles",
}) => {
  const [position, setPosition] = useState({ coordinates: DEFAULT_CENTER, zoom: DEFAULT_ZOOM });
  const [hoveredState, setHoveredState] = useState(null);
  const [hoveredCounty, setHoveredCounty] = useState(null);
  const [selectedState, setSelectedState] = useState(null); // { name: "Alaska", abbr: "AK", fips: "02" }

  // Normalise input.
  // Supports:
  //   - { "AL": 4 } or { "Alabama": 4 }
  //   - [{ name|state|abbreviation, count|value|total, counties|cities: [{ name|county|city, fips, count }] }]
  const { stateCounts, countiesByStateAbbr } = useMemo(() => {
    const counts = {};
    const counties = {};

    const addCounties = (abbrKey, list) => {
      if (!Array.isArray(list) || list.length === 0) return;
      counties[abbrKey] = list
        .map((c) => ({
          name: c.name || c.county || c.city || "",
          fips: c.fips ? String(c.fips).padStart(5, "0") : null,
          count: c.count ?? c.value ?? c.total ?? 0,
        }))
        .filter((c) => c.name || c.fips)
        .sort((a, b) => (b.count || 0) - (a.count || 0));
    };

    if (Array.isArray(data)) {
      data.forEach((item) => {
        if (!item) return;
        const key = item.state || item.name || item.abbreviation;
        if (!key) return;
        const abbr = STATE_ABBR_TO_FIPS[key]
          ? key
          : Object.entries(STATE_ABBR_TO_NAME).find(([, n]) => n === key)?.[0] || key;
        const full = STATE_ABBR_TO_NAME[abbr] || key;
        const value = item.count ?? item.value ?? item.total ?? 0;
        counts[full] = (counts[full] || 0) + (typeof value === "number" ? value : 0);
        addCounties(abbr, item.counties || item.cities);
      });
    } else if (data && typeof data === "object") {
      Object.entries(data).forEach(([key, value]) => {
        const full = STATE_ABBR_TO_NAME[key] || key;
        counts[full] = typeof value === "number" ? value : 0;
      });
    }
    return { stateCounts: counts, countiesByStateAbbr: counties };
  }, [data]);

  const values = Object.values(stateCounts).filter((v) => typeof v === "number" && v > 0);
  const maxValue = values.length ? Math.max(...values) : 0;
  const minValue = values.length ? Math.min(...values) : 0;

  const stateColorScale = useMemo(() => {
    if (maxValue <= 0) return null;
    return scaleLinear()
      .domain([minValue, maxValue])
      .range([`${baseColor}33`, baseColor]);
  }, [minValue, maxValue, baseColor]);

  const fillForState = (stateName) => {
    const v = stateCounts?.[stateName];
    if (typeof v === "number" && v > 0 && stateColorScale) return stateColorScale(v);
    return isLightTheme ? "#e5e7eb" : "#1f2a44";
  };

  const formatValue = (v) => {
    if (typeof valueFormatter === "function") return valueFormatter(v);
    return v.toLocaleString("en-US");
  };

  const selectedCounties = selectedState ? countiesByStateAbbr[selectedState.abbr] || [] : [];
  const selectedCountiesMax = selectedCounties.reduce(
    (m, c) => (typeof c.count === "number" && c.count > m ? c.count : m),
    0
  );

  const countyColorScale = useMemo(() => {
    if (!selectedState || selectedCountiesMax <= 0) return null;
    return scaleLinear()
      .domain([0, selectedCountiesMax])
      .range([`${baseColor}22`, baseColor]);
  }, [baseColor, selectedState, selectedCountiesMax]);

  // Build a lookup so we can match a geography county to an API county quickly.
  const countyLookup = useMemo(() => {
    const byFips = {};
    const byName = {};
    selectedCounties.forEach((c) => {
      if (c.fips) byFips[c.fips] = c;
      if (c.name) byName[normaliseCountyName(c.name)] = c;
    });
    return { byFips, byName };
  }, [selectedCounties]);

  const findCountyData = (geo) => {
    const id = String(geo.id || "").padStart(5, "0");
    if (countyLookup.byFips[id]) return countyLookup.byFips[id];
    const name = geo.properties?.name || "";
    return countyLookup.byName[normaliseCountyName(name)] || null;
  };

  const handleZoomIn = () => {
    if (position.zoom >= 8) return;
    setPosition((pos) => ({ ...pos, zoom: pos.zoom * 1.4 }));
  };

  const handleZoomOut = () => {
    if (position.zoom <= 1) return;
    setPosition((pos) => ({ ...pos, zoom: pos.zoom / 1.4 }));
  };

  const handleReset = () => {
    setPosition({ coordinates: DEFAULT_CENTER, zoom: DEFAULT_ZOOM });
    setSelectedState(null);
    setHoveredCounty(null);
    setHoveredState(null);
  };

  const handleMoveEnd = (pos) => setPosition(pos);

  const handleStateClick = (geo, stateName) => {
    const value = stateCounts?.[stateName];
    if (!value) return;
    const centroid = geoCentroid(geo);
    if (!centroid || Number.isNaN(centroid[0]) || Number.isNaN(centroid[1])) return;
    const fips = String(geo.id || "").padStart(2, "0");
    const abbr = STATE_FIPS_TO_ABBR[fips];
    setSelectedState({ name: stateName, abbr, fips });
    setHoveredState(null);
    setHoveredCounty(null);
    setPosition({ coordinates: centroid, zoom: STATE_ZOOM });
  };

  const titleColor = isLightTheme ? "#1f2937" : "#f1f5f9";
  const tooltipBg = isLightTheme ? "#1f2937" : "#0f172a";
  const tooltipColor = "#ffffff";
  const zoomBtnBg = isLightTheme ? "#1f2937" : "#0f172a";
  const zoomBtnColor = "#ffffff";

  const panelBg = isLightTheme ? "rgba(255,255,255,0.96)" : "rgba(15,23,42,0.94)";
  const panelText = isLightTheme ? "#1f2937" : "#f1f5f9";
  const panelSub = isLightTheme ? "#64748b" : "#94a3b8";
  const panelBorder = isLightTheme ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.08)";
  const barTrack = isLightTheme ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.08)";

  const mapFrameStyle = fillContainer
    ? {
        position: "relative",
        width: "100%",
        flex: "1 1 auto",
        minHeight: 200,
        overflow: "hidden",
      }
    : {
        position: "relative",
        width: "100%",
        height,
        overflow: "hidden",
      };

  const rootStyle = fillContainer
    ? {
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }
    : { width: "100%" };

  return (
    <div className="revenue-by-location-map" style={rootStyle}>
      {title && (
        <div
          className="card-category mb-2"
          style={{ color: titleColor, fontWeight: 600, fontSize: "14px" }}
        >
          {title}
        </div>
      )}
      <div style={mapFrameStyle}>
        <div
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <button
            type="button"
            onClick={handleZoomIn}
            aria-label="Zoom in"
            style={zoomBtnStyle(zoomBtnBg, zoomBtnColor, 18)}
          >
            +
          </button>
          <button
            type="button"
            onClick={handleZoomOut}
            aria-label="Zoom out"
            style={zoomBtnStyle(zoomBtnBg, zoomBtnColor, 18)}
          >
            −
          </button>
          {(position.zoom !== 1 || selectedState) && (
            <button
              type="button"
              onClick={handleReset}
              aria-label="Reset"
              title="Reset"
              style={zoomBtnStyle(zoomBtnBg, zoomBtnColor, 12)}
            >
              ⤾
            </button>
          )}
        </div>

        <ComposableMap
          projection="geoAlbersUsa"
          projectionConfig={{ scale: 900 }}
          width={800}
          height={500}
          style={{ width: "100%", height: "100%" }}
        >
          <ZoomableGroup
            zoom={position.zoom}
            center={position.coordinates}
            onMoveEnd={handleMoveEnd}
            maxZoom={8}
            minZoom={1}
          >
            {/* State layer — faded when a state is drilled-in */}
            <Geographies geography={US_STATES_TOPO_JSON}>
              {({ geographies }) => (
                <>
                  {geographies.map((geo) => {
                    const stateName = geo.properties.name;
                    const value = stateCounts?.[stateName];
                    const hasData = typeof value === "number" && value > 0;
                    const isSelected = selectedState?.name === stateName;
                    const isOtherSelected = selectedState && !isSelected;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onMouseEnter={() => {
                          if (selectedState) return;
                          setHoveredState({
                            name: stateName,
                            value: typeof value === "number" ? value : null,
                          });
                        }}
                        onMouseLeave={() => setHoveredState(null)}
                        onClick={() => !selectedState && handleStateClick(geo, stateName)}
                        style={{
                          default: {
                            fill: isOtherSelected
                              ? isLightTheme
                                ? "#f1f5f9"
                                : "#0f172a"
                              : fillForState(stateName),
                            stroke: STROKE_COLOR,
                            strokeWidth: isSelected ? 1.2 : 0.6,
                            fillOpacity: isOtherSelected ? 0.55 : 1,
                            outline: "none",
                            pointerEvents: selectedState ? "none" : "auto",
                          },
                          hover: {
                            fill: hasData ? DEFAULT_FILL_HOVER : fillForState(stateName),
                            stroke: STROKE_COLOR,
                            strokeWidth: 0.8,
                            outline: "none",
                            cursor: hasData ? "pointer" : "default",
                          },
                          pressed: {
                            fill: DEFAULT_FILL_HOVER,
                            outline: "none",
                          },
                        }}
                      />
                    );
                  })}

                  {/* Count labels on states (hidden when a state is drilled in) */}
                  {!selectedState &&
                    geographies.map((geo) => {
                      const stateName = geo.properties.name;
                      const value = stateCounts?.[stateName];
                      if (!value || value <= 0) return null;
                      const centroid = geoCentroid(geo);
                      if (
                        !centroid ||
                        Number.isNaN(centroid[0]) ||
                        Number.isNaN(centroid[1])
                      ) {
                        return null;
                      }
                      const label = formatValue(value);
                      const fontSize = 13 / Math.max(position.zoom * 0.65, 1);
                      const padX = 4 / Math.max(position.zoom * 0.65, 1);
                      const padY = 2 / Math.max(position.zoom * 0.65, 1);
                      const labelWidth = label.length * (fontSize * 0.6) + padX * 2;
                      const labelHeight = fontSize + padY * 2;
                      return (
                        <Marker
                          key={`label-${geo.rsmKey}`}
                          coordinates={centroid}
                          style={{ pointerEvents: "none" }}
                        >
                          <g style={{ pointerEvents: "none" }}>
                            <rect
                              x={-labelWidth / 2}
                              y={-labelHeight / 2}
                              width={labelWidth}
                              height={labelHeight}
                              fill="#ffffff"
                              stroke={baseColor}
                              strokeWidth={1 / Math.max(position.zoom * 0.65, 1)}
                              opacity={0.95}
                            />
                            <text
                              textAnchor="middle"
                              dominantBaseline="central"
                              style={{
                                fontFamily: "system-ui, sans-serif",
                                fontSize,
                                fontWeight: 700,
                                fill: baseColor,
                                pointerEvents: "none",
                                userSelect: "none",
                              }}
                            >
                              {label}
                            </text>
                          </g>
                        </Marker>
                      );
                    })}
                </>
              )}
            </Geographies>

            {/* County layer — only loaded/rendered when a state is selected */}
            {selectedState && (
              <Geographies geography={US_COUNTIES_TOPO_JSON}>
                {({ geographies }) =>
                  geographies
                    .filter((geo) => String(geo.id || "").startsWith(selectedState.fips))
                    .map((geo) => {
                      const countyData = findCountyData(geo);
                      const v = countyData?.count || 0;
                      const fill =
                        v > 0 && countyColorScale
                          ? countyColorScale(v)
                          : isLightTheme
                            ? "#e5e7eb"
                            : "#334155";
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          onMouseEnter={() =>
                            setHoveredCounty({
                              name: geo.properties?.name || countyData?.name || "",
                              count: countyData?.count || 0,
                              state: selectedState.name,
                              matched: !!countyData,
                            })
                          }
                          onMouseLeave={() => setHoveredCounty(null)}
                          style={{
                            default: {
                              fill,
                              stroke: isLightTheme ? "#ffffff" : "#0f172a",
                              strokeWidth: 0.3,
                              outline: "none",
                            },
                            hover: {
                              fill: countyData ? DEFAULT_FILL_HOVER : fill,
                              stroke: isLightTheme ? "#ffffff" : "#0f172a",
                              strokeWidth: 0.6,
                              outline: "none",
                              cursor: countyData ? "pointer" : "default",
                            },
                            pressed: {
                              fill: DEFAULT_FILL_HOVER,
                              outline: "none",
                            },
                          }}
                        />
                      );
                    })
                }
              </Geographies>
            )}
          </ZoomableGroup>
        </ComposableMap>

        {/* Side panel listing counties — shown when a state is drilled-in */}
        {selectedState && (
          <div
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              bottom: 12,
              width: 220,
              zIndex: 3,
              background: panelBg,
              color: panelText,
              border: `1px solid ${panelBorder}`,
              boxShadow: "0 6px 16px rgba(0,0,0,0.18)",
              backdropFilter: "blur(4px)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              pointerEvents: "auto",
            }}
          >
            <div
              style={{
                padding: "10px 12px",
                borderBottom: `1px solid ${panelBorder}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: panelSub,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  Counties
                </div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={selectedState.name}
                >
                  {selectedState.name}
                </div>
              </div>
              <button
                type="button"
                onClick={handleReset}
                aria-label="Close"
                style={{
                  border: 0,
                  background: "transparent",
                  color: panelSub,
                  cursor: "pointer",
                  fontSize: 18,
                  lineHeight: 1,
                  padding: 4,
                }}
              >
                ×
              </button>
            </div>

            <div style={{ overflowY: "auto", padding: "6px 4px 6px 0" }}>
              {selectedCounties.length === 0 && (
                <div
                  style={{
                    padding: "16px 12px",
                    fontSize: 12,
                    color: panelSub,
                    textAlign: "center",
                  }}
                >
                  No county data available
                </div>
              )}
              {selectedCounties.map((county, i) => {
                const pct =
                  selectedCountiesMax > 0
                    ? ((county.count || 0) / selectedCountiesMax) * 100
                    : 0;
                return (
                  <div
                    key={`${county.name}-${i}`}
                    onMouseEnter={() =>
                      setHoveredCounty({
                        name: county.name,
                        count: county.count,
                        state: selectedState.name,
                        matched: true,
                      })
                    }
                    onMouseLeave={() => setHoveredCounty(null)}
                    style={{ padding: "6px 12px", cursor: "default" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        marginBottom: 4,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={county.name}
                      >
                        {county.name}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: baseColor,
                          flexShrink: 0,
                        }}
                      >
                        {formatValue(county.count || 0)}
                      </span>
                    </div>
                    <div
                      style={{
                        height: 4,
                        background: barTrack,
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${pct}%`,
                          background: baseColor,
                          transition: "width 0.3s ease",
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Hover tooltip — bottom-left so it doesn't clash with the side panel */}
        {(hoveredCounty || (hoveredState && !selectedState)) && (
          <div
            style={{
              position: "absolute",
              bottom: 12,
              left: 12,
              background: tooltipBg,
              color: tooltipColor,
              padding: "6px 10px",
              fontSize: 12,
              fontWeight: 500,
              pointerEvents: "none",
              boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
              maxWidth: 200,
              zIndex: 4,
            }}
          >
            {hoveredCounty ? (
              <>
                <div style={{ fontWeight: 700 }}>{hoveredCounty.name}</div>
                <div style={{ opacity: 0.75, marginTop: 2, fontSize: 10 }}>
                  {hoveredCounty.state}
                </div>
                <div style={{ opacity: 0.9, marginTop: 2 }}>
                  {formatValue(hoveredCounty.count || 0)} {valueLabel}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 700 }}>{hoveredState.name}</div>
                <div style={{ opacity: 0.85, marginTop: 2 }}>
                  {hoveredState.value !== null
                    ? `${formatValue(hoveredState.value)} ${valueLabel}`
                    : `0 ${valueLabel}`}
                </div>
                {hoveredState.value > 0 && (
                  <div style={{ opacity: 0.6, marginTop: 4, fontSize: 10 }}>
                    Click to drill in
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const zoomBtnStyle = (bg, color, fontSize) => ({
  width: 28,
  height: 28,
  border: 0,
  background: bg,
  color,
  cursor: "pointer",
  fontSize,
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
});

export default RevenueByLocationMap;
